/**
 * Web QR scanner — browser camera + jsQR, with an upload fallback.
 * Only loaded on web (Metro resolves .web.tsx for web builds).
 *
 * Both transfer screens use this, and on a phone the camera choice is the
 * whole game: the QR code is on ANOTHER device, so only the rear camera can
 * see it. This used to ask for `facingMode: 'user'` — the selfie camera —
 * which is also the only camera a laptop has, so it went unnoticed until
 * someone tried it on an iPhone. `ideal` rather than `exact` below is what
 * keeps the laptop working: a device with no rear camera still resolves to
 * the one it has instead of throwing OverconstrainedError.
 *
 * The upload fallback is safe by design and is the path a laptop user takes:
 * QR #1 is a public key and QR #2 is sealed to the target's ephemeral key, so
 * a screenshot of either is worthless to anyone else (see key-transfer.ts).
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import jsQR from 'jsqr';
import { useTheme } from '../theme/ThemeContext';
import { useThemedStyles } from '../theme/useThemedStyles';
import type { Theme } from '../theme/types';

interface WebQRScannerProps {
  onScan: (data: string) => void;
  onBack: () => void;
  isProcessing: boolean;
  error: string | null;
}

type Mode = 'choose' | 'camera';
type Facing = 'environment' | 'user';

/**
 * Long-edge cap for uploaded images before decoding. iOS Safari silently
 * returns a BLANK canvas past its backing-store limit (roughly 16 M pixels),
 * and a 12 MP camera photo sits right at it — jsQR then found nothing and the
 * user was told to "try a clearer image". A QR code decodes fine at this size.
 */
const MAX_UPLOAD_EDGE = 1600;

const describe = (err: unknown): string =>
  err instanceof Error ? err.message : String(err);

function cameraErrorMessage(err: unknown): string {
  if (err instanceof DOMException) {
    switch (err.name) {
      case 'NotAllowedError':
        return 'Camera access was denied. Allow the camera for this site in your browser settings, or upload a screenshot of the code instead.';
      case 'NotFoundError':
      case 'OverconstrainedError':
        return 'No usable camera was found. Upload a screenshot of the code instead.';
      case 'NotReadableError':
        return 'The camera is in use by another app or tab. Close it and try again, or upload a screenshot of the code instead.';
    }
  }
  return `Could not access the camera (${describe(err)}). Upload a screenshot of the code instead.`;
}

function stopTracks(stream: MediaStream | null): void {
  stream?.getTracks().forEach((t) => t.stop());
}

export default function WebQRScanner({
  onScan,
  onBack,
  isProcessing,
  error,
}: WebQRScannerProps) {
  const [mode, setMode] = useState<Mode>('choose');
  const [facing, setFacing] = useState<Facing>('environment');
  const [canSwitch, setCanSwitch] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  // Bumped whenever a new stream lands in `streamRef`, so the attach effect
  // below re-runs for a camera switch even though `mode` does not change.
  const [streamGen, setStreamGen] = useState(0);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const scannedRef = useRef(false);
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();

  const displayError = error || localError;

  const stopCamera = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    stopTracks(streamRef.current);
    streamRef.current = null;
  }, []);

  useEffect(() => {
    return () => stopCamera();
  }, [stopCamera]);

  const scanFrame = useCallback(() => {
    if (!videoRef.current || !canvasRef.current || scannedRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (video.readyState !== video.HAVE_ENOUGH_DATA) {
      animFrameRef.current = requestAnimationFrame(scanFrame);
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height);

    if (code) {
      scannedRef.current = true;
      stopCamera();
      onScan(code.data);
      return;
    }

    animFrameRef.current = requestAnimationFrame(scanFrame);
  }, [onScan, stopCamera]);

  // Attach the stream once the <video> is actually in the DOM. This used to be
  // a one-shot requestAnimationFrame fired right after setMode('camera'),
  // racing React's commit: when the ref was still null the `if` was simply
  // skipped — no retry, no error — leaving the header saying "Scanning...",
  // the video black, the iOS camera light ON, and the scan loop never started.
  // An effect keyed on `mode` runs after commit, when the element exists.
  useEffect(() => {
    if (mode !== 'camera') return;
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream) return;

    let cancelled = false;
    video.srcObject = stream;
    video
      .play()
      .then(() => {
        if (cancelled) return;
        animFrameRef.current = requestAnimationFrame(scanFrame);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // A rejected play() was previously unhandled: the same black video
        // and lit camera light, plus a crash-shaped unhandled rejection in
        // telemetry. Release the camera and say what happened.
        stopCamera();
        setMode('choose');
        setLocalError(
          `The camera stream could not start (${describe(err)}). Try again, or upload a screenshot of the code instead.`
        );
      });

    return () => {
      cancelled = true;
    };
  }, [mode, streamGen, scanFrame, stopCamera]);

  const acquireStream = (want: Facing): Promise<MediaStream> =>
    navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: want } },
    });

  // Whether there is a second camera to switch to. Best-effort: labels need a
  // granted permission, but the count does not, and a wrong answer only hides
  // or shows a button.
  const detectSwitchable = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      setCanSwitch(devices.filter((d) => d.kind === 'videoinput').length > 1);
    } catch {
      setCanSwitch(false);
    }
  };

  const startCamera = async () => {
    setLocalError(null);

    // `navigator.mediaDevices` is simply absent on an insecure origin, which is
    // what a LAN dev server over plain http is. That is not "this browser" —
    // iOS Safari supports the camera perfectly well — so name the real cause.
    if (typeof window !== 'undefined' && window.isSecureContext === false) {
      setLocalError(
        'The camera needs a secure (https) address. This page was opened over plain http, so the browser will not offer it. Upload a screenshot of the code instead.'
      );
      return;
    }
    if (!navigator?.mediaDevices?.getUserMedia) {
      setLocalError(
        'This browser does not support camera access. Upload a screenshot of the code instead.'
      );
      return;
    }

    try {
      // Called synchronously within the press, as iOS requires: the permission
      // prompt only appears inside a user gesture.
      const stream = await acquireStream(facing);
      streamRef.current = stream;
      setStreamGen((g) => g + 1);
      setMode('camera');
      void detectSwitchable();
    } catch (err) {
      setLocalError(cameraErrorMessage(err));
    }
  };

  const switchCamera = async () => {
    const next: Facing = facing === 'environment' ? 'user' : 'environment';
    setLocalError(null);

    // Release the current camera BEFORE asking for the other one — iOS will
    // not hand out a second stream while the first is still open.
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    stopTracks(streamRef.current);
    streamRef.current = null;

    try {
      const stream = await acquireStream(next);
      streamRef.current = stream;
      setFacing(next);
      setStreamGen((g) => g + 1);
    } catch (err) {
      setMode('choose');
      setLocalError(`Could not switch cameras. ${cameraErrorMessage(err)}`);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLocalError(null);

    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, MAX_UPLOAD_EDGE / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) {
          setLocalError('Failed to process image.');
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);

        if (code) {
          scannedRef.current = true;
          onScan(code.data);
        } else {
          setLocalError(
            'No QR code found in that image. Use a screenshot of the code, or a photo where the whole code is in frame.'
          );
        }
      };
      img.onerror = () => {
        setLocalError('Failed to load image. Please try a different file.');
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);

    // Reset so re-selecting the same file triggers onChange
    event.target.value = '';
  };

  // Hidden DOM elements (always rendered for ref access)
  const hiddenElements = (
    <>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileUpload}
        // A raw DOM element, so testID would not reach it. The synthetic
        // monitor drives the two-QR handshake through this input with
        // screenshots of the other context's code — no camera in CI.
        data-testid="qr-scanner.file-input"
      />
    </>
  );

  if (mode === 'camera') {
    return (
      <View style={styles.card}>
        {hiddenElements}
        <Text
          style={styles.title}
          testID="qr-scanner.web-title"
          accessibilityRole="header"
        >
          Scanning...
        </Text>
        <Text style={styles.subtitle}>
          Point your camera at the QR code on your other device
        </Text>

        <View style={styles.videoContainer}>
          <video
            ref={videoRef}
            style={{
              width: '100%',
              maxWidth: 400,
              borderRadius: 12,
            }}
            playsInline
            autoPlay
            muted
          />
        </View>

        {canSwitch && (
          <TouchableOpacity
            style={styles.switchButton}
            onPress={switchCamera}
            activeOpacity={0.7}
            accessibilityLabel={
              facing === 'environment' ? 'Switch to front camera' : 'Switch to rear camera'
            }
            accessibilityRole="button"
            testID="qr-scanner.switch-camera-button"
          >
            <Text style={styles.switchButtonText}>
              {facing === 'environment' ? 'Use Front Camera' : 'Use Rear Camera'}
            </Text>
          </TouchableOpacity>
        )}

        {isProcessing && (
          <View style={styles.processingBox}>
            <ActivityIndicator size="small" color={colors.primary} />
            <Text style={styles.processingText}> Importing key...</Text>
          </View>
        )}

        {displayError && (
          <View style={styles.errorBox} testID="qr-scanner.web-error">
            <Text style={styles.errorText}>{displayError}</Text>
          </View>
        )}

        <TouchableOpacity
          style={styles.backButton}
          onPress={() => {
            stopCamera();
            scannedRef.current = false;
            setLocalError(null);
            setMode('choose');
          }}
          activeOpacity={0.7}
          accessibilityLabel="Stop scanning"
          accessibilityRole="button"
          testID="qr-scanner.stop-camera-button"
        >
          <Text style={styles.backButtonText}>Cancel</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // Mode: 'choose'
  return (
    <View style={styles.card}>
      {hiddenElements}
      <View style={styles.iconContainer}>
        <Text style={styles.iconText}>{'\u{1F4F7}'}</Text>
      </View>
      <Text
        style={styles.title}
        testID="qr-scanner.web-title"
        accessibilityRole="header"
      >
        Scan QR Code
      </Text>
      <Text style={styles.subtitle}>
        Scan the QR code displayed on your other device to transfer your
        encryption key.
      </Text>

      <TouchableOpacity
        style={styles.optionButton}
        onPress={startCamera}
        activeOpacity={0.7}
        accessibilityLabel="Scan with camera"
        accessibilityRole="button"
        testID="qr-scanner.webcam-button"
      >
        <Text style={styles.optionIcon} accessible={false}>
          {'\u{1F3A5}'}
        </Text>
        <View style={styles.optionTextContainer} accessible={false}>
          <Text style={styles.optionTitle} accessible={false}>
            Use Camera
          </Text>
          <Text style={styles.optionSubtitle} accessible={false}>
            Point your camera at the QR code
          </Text>
        </View>
        <Text style={styles.optionArrow} accessible={false}>
          {'›'}
        </Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={styles.optionButton}
        onPress={() => fileInputRef.current?.click()}
        activeOpacity={0.7}
        accessibilityLabel="Upload QR code image"
        accessibilityRole="button"
        testID="qr-scanner.upload-button"
      >
        <Text style={styles.optionIcon} accessible={false}>
          {'\u{1F4C1}'}
        </Text>
        <View style={styles.optionTextContainer} accessible={false}>
          <Text style={styles.optionTitle} accessible={false}>
            Upload Image
          </Text>
          <Text style={styles.optionSubtitle} accessible={false}>
            Select a screenshot of the QR code
          </Text>
        </View>
        <Text style={styles.optionArrow} accessible={false}>
          {'›'}
        </Text>
      </TouchableOpacity>

      {isProcessing && (
        <View style={styles.processingBox}>
          <ActivityIndicator size="small" color={colors.primary} />
          <Text style={styles.processingText}> Importing key...</Text>
        </View>
      )}

      {displayError && (
        <View style={styles.errorBox} testID="qr-scanner.web-error">
          <Text style={styles.errorText}>{displayError}</Text>
        </View>
      )}

      <TouchableOpacity
        style={styles.backButton}
        onPress={onBack}
        activeOpacity={0.7}
        accessibilityLabel="Go back"
        accessibilityRole="button"
        testID="qr-scanner.back-button-web"
      >
        <Text style={styles.backButtonText}>Go Back</Text>
      </TouchableOpacity>
    </View>
  );
}

const createStyles = (theme: Theme) => ({
  card: {
    backgroundColor: theme.colors.surface,
    borderRadius: 16,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 5,
  },
  iconContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: theme.colors.primaryBg,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    alignSelf: 'center' as const,
    marginBottom: 16,
  },
  iconText: { fontSize: 28 },
  title: {
    fontSize: 20,
    fontWeight: '700' as const,
    color: theme.colors.textPrimary,
    textAlign: 'center' as const,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    color: theme.colors.textMuted,
    textAlign: 'center' as const,
    marginBottom: 20,
    lineHeight: 20,
  },
  optionButton: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    backgroundColor: theme.colors.primaryBg,
    borderWidth: 1,
    borderColor: theme.colors.primaryLight,
    borderRadius: 12,
    padding: 14,
    marginBottom: 12,
  },
  optionIcon: { fontSize: 28, marginRight: 12 },
  optionTextContainer: { flex: 1 as const },
  optionTitle: {
    fontSize: 16,
    fontWeight: '700' as const,
    color: theme.colors.primaryDark,
    marginBottom: 2,
  },
  optionSubtitle: {
    fontSize: 13,
    color: theme.colors.primary,
    lineHeight: 18,
  },
  optionArrow: {
    fontSize: 24,
    color: theme.colors.primaryLight,
    fontWeight: '700' as const,
  },
  videoContainer: {
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    minHeight: 300,
    marginBottom: 16,
    borderRadius: 12,
    overflow: 'hidden' as const,
    backgroundColor: '#000',
  },
  switchButton: {
    backgroundColor: theme.colors.primaryBg,
    borderWidth: 1,
    borderColor: theme.colors.primaryLight,
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: 'center' as const,
    marginBottom: 12,
  },
  switchButtonText: {
    color: theme.colors.primary,
    fontSize: 15,
    fontWeight: '600' as const,
  },
  processingBox: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    marginBottom: 12,
  },
  processingText: {
    color: theme.colors.textMuted,
    fontSize: 14,
    fontWeight: '600' as const,
  },
  errorBox: {
    backgroundColor: theme.colors.errorBg,
    borderWidth: 1,
    borderColor: theme.colors.errorBorder,
    borderRadius: 10,
    padding: 14,
    marginBottom: 12,
  },
  errorText: {
    color: theme.colors.errorText,
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center' as const,
  },
  backButton: {
    borderWidth: 1,
    borderColor: theme.colors.borderMedium,
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: 'center' as const,
  },
  backButtonText: {
    color: theme.colors.textSecondary,
    fontSize: 16,
    fontWeight: '600' as const,
  },
});
